import {
  TransactionDialog,
  transactionInner,
} from "@/components/transaction/transaction-dialog";
import { AlertDialogHeader } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { WithErrorAndLoading } from "@/components/with-error-and-loading";
import { formatNumber, shorten } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  UserRound,
  ChevronRight,
  ChevronLeft,
  AlertCircleIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useWallet } from "@txnlab/use-wallet";
import { algod } from "@/lib/algo";
import { makeSwapTransactions } from "@/lib/clients";
import { CopyButton } from "@/components/copy-button";
import { useAlgoAcctInfo } from "@/hooks/use-algo-account";

type OnClick = (event: any) => void;

export const snakeCaseToCamel = (str: string) =>
  str.replace(/_./g, (s) => s[1].toUpperCase());

async function getLocalState(acct: any) {
  const raw = acct["apps-local-state"][0]["key-value"];
  // @ts-ignore
  return raw.reduce((out, { key, value: { uint, bytes } }) => {
    const newKey = snakeCaseToCamel(Buffer.from(key, "base64").toString());
    out[newKey] = bytes ? bytes : uint;
    return out;
  }, {});
}

export async function getQuote(amount: number, tinymanEscrow: string) {
  const acct = await algod.accountInformation(tinymanEscrow).do();

  let { asset1Reserves, asset2Reserves } = await getLocalState(acct);

  const inputAmount = amount;

  const fee = (ia: number) => Math.floor((ia * 30) / 10_000);

  const swapOutput =
    BigInt(asset2Reserves) -
    BigInt(
      Math.floor(
        (asset1Reserves * asset2Reserves) /
          (asset1Reserves + inputAmount - fee(inputAmount))
      )
    ) +
    BigInt(1);

  return Math.floor(Number(swapOutput) * 1.03);
}

export function SwapDialog({
  outAssetId,
  outAssetUnitName,
  outAssetDecimals,
  tinymanEscrow,
  tinymanAppId,
  outAmount: initialOutAmount,
  trigger,
  needsOptin,
}: {
  outAssetId: number;
  outAssetUnitName: string;
  outAssetDecimals: number;
  outAmount: number;
  tinymanEscrow: string;
  tinymanAppId: number;
  trigger: (o: OnClick) => React.ReactNode;
  needsOptin: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inputAmount, setInputAmount] = useState(0);
  const [outputAmount, setOutputAmount] = useState(0);

  const { activeAddress } = useWallet();

  useEffect(() => {
    if (open) {
      setLoading(true);
      setInputAmount(0);
      setOutputAmount(0);
      setWaitForSwap(false);
      setError("");
      (async () => {
        try {
          const inputAmount = await getQuote(initialOutAmount, tinymanEscrow);
          if (inputAmount < 1000) {
            const newInputAmount = 1000;
            setInputAmount(newInputAmount);
            setOutputAmount(
              Math.floor((newInputAmount / inputAmount) * initialOutAmount)
            );
          } else {
            setInputAmount(inputAmount);
            setOutputAmount(initialOutAmount);
          }
        } catch (e) {
          setError((e as Error).message);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [open]);

  const { refetch } = useAlgoAcctInfo(activeAddress);

  const onSuccess = () => {
    setOpen(false);
    refetch();
  };

  const [waitForSwap, setWaitForSwap] = useState(false);

  const goVestige = () => {
    setWaitForSwap(true);
    window.open(`https://vestige.fi/swap?asset_in=0&asset_out=${outAssetId}`);
  };

  return (
    <AlertDialog open={open}>
      <AlertDialogTrigger asChild>
        {trigger(() => setOpen(true))}
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-[95vw] md:max-w-md md:min-w-[550px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="mb-2 text-center text-foreground/80">
            {waitForSwap
              ? `Waiting for ${outAssetUnitName} swap`
              : `Swap ALGO to ${outAssetUnitName}`}
          </AlertDialogTitle>
        </AlertDialogHeader>
        <WithErrorAndLoading noLogo={true} loading={loading} error={error}>
          <div className="flex flex-col gap-4">
            {waitForSwap ? (
              <>
                <div className="">
                  Go to Vestige.fi to swap and then press{" "}
                  <code className="font-bold text-lg">Done</code> when your swap
                  is completed.
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">
                    {outAssetUnitName} amount:{" "}
                  </span>
                  {formatNumber(
                    outputAmount * 1.010101,
                    outAssetDecimals,
                    outAssetDecimals
                  )}{" "}
                  <CopyButton
                    value={(
                      (outputAmount * 1.010101) /
                      10 ** outAssetDecimals
                    ).toString()}
                  />{" "}
                </div>
                <div className="flex items-center gap-2 -mt-4">
                  <span className="text-muted-foreground">Slippage: </span>
                  1% <span className="text-muted-foreground">(default)</span>
                </div>
              </>
            ) : (
              <>
                <div>
                  Do you want to buy{" "}
                  {formatNumber(
                    outputAmount,
                    outAssetDecimals,
                    outAssetDecimals
                  )}{" "}
                  {outAssetUnitName} for {formatNumber(inputAmount, 6, 6)} ALGO?
                </div>
                <div className="text-sm text-muted-foreground">
                  Using the Tinyman v2 ALGO/{outAssetUnitName} pool with 2%
                  slippage
                </div>
                {inputAmount < 10_000_000n ? null : (
                  <div className="text-foreground bg-card border p-4 pt-3 rounded-xl flex flex-col gap-1">
                    <div className="font-bold flex items-center gap-1">
                      <AlertCircleIcon size={20} className="inline mr-2" />
                      Warning
                    </div>
                    <div>
                      This is a convenience swapper that does{" "}
                      <strong>not</strong> swap efficiently.
                    </div>
                    <div>
                      For large amounts, prefer to swap using a more efficient
                      swap router.{" "}
                    </div>
                    <div>
                      <span
                        className="link"
                        onClick={() =>
                          waitForSwap ? goVestige() : setWaitForSwap(true)
                        }
                      >
                        Click here
                      </span>{" "}
                      to perform this swap on Vestige.fi.
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex gap-2 mt-2">
            {!waitForSwap ? (
              <div
                className="link text-muted-foreground text-sm"
                onClick={() =>
                  waitForSwap ? goVestige() : setWaitForSwap(true)
                }
              >
                Not working?
              </div>
            ) : null}
            <div className="flex-grow" />
            <Button
              variant={"outline"}
              onClick={() => (waitForSwap ? onSuccess() : setOpen(false))}
            >
              {waitForSwap ? "Done" : "Close"}
            </Button>
            {waitForSwap ? (
              <Button variant={"default"} onClick={() => goVestige()}>
                Go to Vestige.fi
              </Button>
            ) : null}

            <TransactionDialog
              type="swap"
              trigger={(onClick) =>
                open && !waitForSwap ? (
                  <Button
                    onClick={(e) => {
                      onClick(e);
                    }}
                  >
                    Buy
                  </Button>
                ) : null
              }
              transactionBuilder={() =>
                makeSwapTransactions(
                  tinymanAppId,
                  tinymanEscrow,
                  activeAddress!,
                  inputAmount,
                  outAssetId,
                  outputAmount,
                  needsOptin
                )
              }
              title={`Swap ALGO to ${outAssetUnitName}`}
              onSuccess={onSuccess}
              // TODO onClose
            >
              {transactionInner(
                "swap",
                <div className="flex flex-col">
                  <div>
                    You are going to{" "}
                    <strong>
                      buy{" "}
                      {formatNumber(
                        outputAmount,
                        outAssetDecimals,
                        outAssetDecimals
                      )}{" "}
                      {outAssetUnitName}
                    </strong>{" "}
                    for {formatNumber(inputAmount, 6, 6)} ALGO.
                  </div>
                </div>,
                <>
                  <div className="flex justify-between gap-2 mb-2">
                    <div className="flex items-center">
                      <UserRound size={18} className="mr-1" /> Account
                    </div>{" "}
                    <span>{shorten(activeAddress!)}</span>
                  </div>
                  {needsOptin ? (
                    <div className="flex justify-between gap-2 mb-2">
                      <div className="flex items-center">
                        <ChevronRight size={18} className="mr-1" /> Opting In
                      </div>
                      <span>{outAssetUnitName}</span>
                    </div>
                  ) : null}
                  <div className="flex justify-between gap-2">
                    <div className="flex items-center">
                      <ChevronRight size={18} className="mr-1" /> Sending
                    </div>
                    <span>{formatNumber(inputAmount, 6, 6)} ALGO</span>
                  </div>
                  <div className="flex justify-between gap-2 mt-2">
                    <div className="flex items-center">
                      <ChevronRight size={18} className="mr-1" /> Swapping
                    </div>
                    <span>Tinyman</span>
                  </div>
                  <div className="flex justify-between gap-2 -mt-1">
                    <div className="flex items-center">
                      <ChevronLeft size={18} className="mr-1 ml-3" /> Receiving
                    </div>{" "}
                    <span>
                      {formatNumber(
                        outputAmount,
                        outAssetDecimals,
                        outAssetDecimals
                      )}{" "}
                      {outAssetUnitName}
                    </span>
                  </div>
                </>
              )}
            </TransactionDialog>
          </div>
        </WithErrorAndLoading>
      </AlertDialogContent>
    </AlertDialog>
  );
}
